import { LitElement, css, html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { MAX_LEGEND_LABEL, legendSymbolKey, type DrawingLegendItem } from '../../shared/drawing-legend.js';
import type { Stroke } from '../../types/app.js';
import type { LegendEdit } from '../drawing-legend-actions.js';
import { drawingLegendSwatch } from '../drawing-legend-symbol.js';
import { t } from '../i18n.js';
import { showContextMenu } from '../context-menu.js';
import { isStrokeEditable, type DrawingLayer } from '../../shared/drawing-layer.js';
import { legendPickerStyles, renderLegendPicker, trackLegendPickerViewport } from './drawing-legend-picker.js';

export class DrawingLegendElement extends LitElement {
  static override properties = {
    layers: { attribute: false }, showLayers: { attribute: false }, drawings: { attribute: false }, items: { attribute: false }, selected: { attribute: false }, canEdit: { type: Boolean },
    editing: { state: true }, adding: { type: Boolean, reflect: true }, collapsed: { state: true }, error: { state: true },
  };
  declare layers: DrawingLayer[];
  declare showLayers: boolean;
  declare items: DrawingLegendItem[];
  declare drawings: Stroke[];
  declare selected: Stroke[];
  declare canEdit: boolean;
  declare private editing: boolean;
  declare private adding: boolean;
  declare private collapsed: boolean;
  declare private error: string;
  private stopViewport?: () => void;
  private closeMenu?: () => void;
  private draft = '';
  private targets: string[] = [];
  apply: (edit: LegendEdit) => string | null = () => 'legend.read_only';
  selectItems: (ids: string[]) => string | null = () => 'legend.read_only';
  onOpen: () => void = () => {};

  constructor() {
    super(); this.layers = []; this.showLayers = false; this.drawings = []; this.items = []; this.selected = []; this.canEdit = false;
    this.editing = false; this.adding = false; this.collapsed = false; this.error = '';
  }

  static override styles = [css`
    .layer-option { display: flex; align-items: center; gap: 10px; min-height: 44px; margin-bottom: 8px; cursor: pointer; }
    .layer-option input { width: 18px; height: 18px; margin: 0; accent-color: #2563eb; }
    :host { position: fixed; right: 16px; bottom: calc(var(--layout-floating-control-bottom, 80px) + 44px + var(--layout-gap, 8px)); z-index: var(--layer-floating-panel, 300); color: var(--text-primary, #17243a); font: 14px/1.4 system-ui, sans-serif; max-width: calc(100vw - 32px); pointer-events: auto; }
    * { box-sizing: border-box; }
    .card { width: 304px; max-width: 100%; background: var(--bg-toolbar, white); border: 1px solid var(--border-color, #dbe2ec); border-radius: 14px; box-shadow: 0 6px 24px #17243a24; padding: 14px; }
    header, .row, .actions, .symbols { display: flex; align-items: center; gap: 8px; }
    header { justify-content: space-between; margin-bottom: 8px; }
    h2 { margin: 0; font-size: 16px; }
    button, input, summary { font: inherit; }
    button, summary { cursor: pointer; }
    button { min-height: 36px; border: 1px solid var(--border-color, #dbe2ec); border-radius: 8px; padding: 6px 10px; background: var(--bg-toolbar, white); color: inherit; }
    button:hover { background: var(--bg-secondary, #f1f5f9); }
    button:focus-visible, input:focus-visible, summary:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
    button:disabled { opacity: .45; cursor: default; }
    .primary { background: #2563eb; border-color: #2563eb; color: white; }
    .primary:hover { background: #1d4ed8; }
    .entry { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; margin-bottom: 8px; padding: 10px 14px; border-radius: 12px; font-size: 13px; font-weight: 600; line-height: 20px; box-shadow: var(--editor-panel-shadow, 0 2px 8px #17243a1f); }
    .entry svg { width: 18px; height: 18px; flex: none; color: var(--text-secondary, #52617a); }
    .entry:hover { border-color: #94a3b8; }
    .entry:active { background: var(--bg-secondary, #e8eef5); }
    ul { list-style: none; padding: 0; margin: 0; max-height: min(42vh, 360px); overflow-y: auto; overscroll-behavior: contain; }
    li { position: relative; padding: 4px 0; }
    .legend-item { border: 1px solid var(--border-color, #dbe2ec); border-radius: 10px; overflow: hidden; }
    .item-button, .static-item { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px; padding: 8px 10px; border: 0; border-radius: 9px; background: transparent; text-align: start; }
    .item-button { font-weight: 500; }
    .item-button:hover, .symbol-button:hover { background: transparent; }
    .item-button:focus-visible { outline: none; }
    .item-button:focus-visible .label { text-decoration: underline; text-underline-offset: 4px; }
    .symbol-button:focus-visible { outline-offset: -3px; }
    .count { flex: none; padding: 1px 6px; border-radius: 6px; background: var(--bg-secondary, #f1f5f9); color: var(--text-secondary, #52617a); font-size: 12px; font-variant-numeric: tabular-nums; }
    .symbol-options { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 8px 8px; }
    .symbol-button { display: grid; place-items: center; min-width: 44px; min-height: 36px; padding: 4px; border: 0; background: transparent; }
    .item-symbol { flex: none; display: flex; align-items: center; }
    .item-symbol svg { display: block; }
    .editing-row { min-height: 44px; padding-right: 52px; }
    .symbols { flex-wrap: wrap; flex: 0 0 42px; gap: 2px; }
    .symbols svg { display: block; }
    .label { overflow-wrap: anywhere; flex: 1; min-width: 0; }
    input { width: 100%; min-width: 0; min-height: 40px; padding: 7px; border: 1px solid var(--border-color, #cbd5e1); border-radius: 7px; background: var(--bg-toolbar, white); color: inherit; }
    .row input { flex: 1; width: 0; }
    .item-actions { position: absolute; right: 0; top: 4px; display: grid; place-items: center; width: 44px; height: 44px; padding: 0; border: 0; border-radius: 8px; background: transparent; }
    .item-actions:focus-visible { outline-offset: -3px; }
    .add { margin-top: 10px; width: 100%; color: var(--editor-focus, #2563eb); }
    p { margin: 10px 0; font-size: 12px; color: var(--text-secondary, #52617a); }
    .error { color: #b91c1c; }
    form { border-top: 1px solid var(--border-color, #dbe2ec); padding-top: 12px; margin-top: 8px; }
    label { display: block; margin-bottom: 8px; }
    label input { margin-top: 5px; }
    .existing { display: flex; align-items: center; width: 100%; gap: 8px; margin-top: 6px; text-align: start; }
    .empty { padding: 4px 0; }
    @media (width <= 768px) {
      :host { right: 12px; top: calc(var(--layout-occupied-top, 176px) + 12px); bottom: auto; max-width: calc(100vw - 24px); }
      .card { width: min(340px, calc(100vw - 24px)); max-height: calc(100dvh - var(--layout-occupied-top, 176px) - var(--layout-occupied-bottom, 64px) - 24px); overflow-y: auto; }
      button, summary, .symbol-button { min-height: 44px; }
      ul { max-height: 26dvh; }
      .entry { display: none; }
      .entry.reader { display: inline-flex; }
    }
    @media print { .edit-control, form, .entry { display: none; } ul { max-height: none; overflow: visible; } }
  `, legendPickerStyles];

  show(): void {
    if (!this.items.length) { this.open(this.selected.length > 0); return; }
    this.onOpen();
    this.collapsed = false; this.editing = false; this.adding = false; this.error = '';
  }

  open(add = false): void {
    if (!this.canEdit) return;
    this.onOpen();
    this.editing = true; this.collapsed = false; this.error = '';
    if (add) this.startAdding();
  }

  private startAdding(): void {
    this.adding = true; this.error = '';
    this.targets = this.selected.filter(stroke => isStrokeEditable(stroke, this.layers)).map(stroke => stroke.id);
    this.draft = this.selected.length === 1 ? (this.selected[0].text?.trim() ?? '').slice(0, MAX_LEGEND_LABEL) : '';
    if (window.innerWidth > 768) void this.updateComplete.then(() => this.renderRoot.querySelector<HTMLInputElement>('#legend-label')?.focus());
  }

  private change(edit: LegendEdit): boolean {
    const failure = this.apply(edit);
    this.error = failure ? t(failure) : '';
    this.requestUpdate();
    return !failure;
  }

  private finish(): void {
    if (this.error) return;
    this.editing = false; this.adding = false;
  }

  private submit(existingId?: string): void {
    if (this.change({ type: 'add', drawingIds: this.targets, label: this.draft, existingId })) {
      this.adding = false; this.draft = ''; this.targets = [];
    }
  }

  private selectedAll(strokes: Stroke[]): boolean {
    return strokes.length > 0 && strokes.every(stroke => this.selected.some(selected => selected.id === stroke.id));
  }

  private activate(strokes: Stroke[]): void {
    const failure = this.selectItems(strokes.map(stroke => stroke.id));
    this.error = failure ? t(failure) : '';
  }

  private symbolChoices(item: DrawingLegendItem) {
    return html`<div class="symbol-options">${item.symbols.map((symbol, index) => {
      const strokes = item.strokes.filter(stroke => legendSymbolKey(stroke) === legendSymbolKey(symbol));
      const label = t('legend.select_symbol').replace('{label}', item.label).replace('{number}', String(index + 1));
      return this.canEdit && strokes.length ? html`<button class="symbol-button" aria-label=${label} title=${label} aria-pressed=${String(this.selectedAll(strokes))}
        @click=${() => this.activate(strokes)}>${unsafeSVG(drawingLegendSwatch(symbol))}</button>`
        : html`<span class="item-symbol">${unsafeSVG(drawingLegendSwatch(symbol))}</span>`;
    })}</div>`;
  }

  private displayRow(item: DrawingLegendItem) {
    const active = item.strokes.some(stroke => this.selected.some(selected => selected.id === stroke.id));
    const content = html`${item.symbols.length === 1 ? html`<span class="item-symbol" aria-hidden="true">${unsafeSVG(drawingLegendSwatch(item.symbols[0]))}</span>` : nothing}
      <span class="label">${item.label}</span>${item.strokes.length > 1 ? html`<span class="count" title=${t('legend.drawing_count').replace('{count}', String(item.strokes.length))}>${item.strokes.length}</span>` : nothing}`;
    return html`<li><div class=${active ? 'legend-item active' : 'legend-item'}>
      ${this.canEdit && item.strokes.length ? html`<button class="item-button" aria-label=${t('legend.select_item').replace('{label}', item.label)} aria-pressed=${String(this.selectedAll(item.strokes))} @click=${() => this.activate(item.strokes)}>${content}</button>`
        : html`<div class="static-item">${content}</div>`}
      ${item.symbols.length > 1 ? this.symbolChoices(item) : nothing}
    </div></li>`;
  }

  private row(item: DrawingLegendItem, index: number) {
    const edit = this.editing && this.canEdit;
    if (!edit || item.layer) return this.displayRow(item);
    return html`<li><div class=${edit ? 'row editing-row' : 'row'}>
      <span class="symbols" aria-hidden="true">${item.symbols.map(stroke => unsafeSVG(drawingLegendSwatch(stroke)))}</span>
      ${edit ? html`<input aria-label=${t('legend.item_label').replace('{number}', String(index + 1))} maxlength=${MAX_LEGEND_LABEL} .value=${item.label}
        @change=${(event: Event) => { if (event.target instanceof HTMLInputElement) this.change({ type: 'rename', id: item.id, label: event.target.value }); }}>`
        : html`<span class="label">${item.label}</span>`}
      </div><button class="item-actions" aria-haspopup="menu" aria-label=${t('legend.item_actions').replace('{label}', item.label)}
        @click=${(event: MouseEvent) => this.openItemMenu(event, item, index)}>•••</button>
      ${edit && item.symbols.length > 1 ? html`<p>${t('legend.mixed')}</p>` : nothing}</li>`;
  }

  private openItemMenu(event: MouseEvent, item: DrawingLegendItem, index: number): void {
    if (!(event.currentTarget instanceof HTMLButtonElement)) return;
    const trigger = event.currentTarget;
    const bounds = trigger.getBoundingClientRect();
    this.closeMenu = showContextMenu({
      ownerDocument: this.ownerDocument, anchor: { x: bounds.right, y: bounds.bottom },
      label: t('legend.item_actions').replace('{label}', item.label),
      items: [
        { command: 'up', label: t('legend.move_up'), icon: 'arrow-up', disabled: index === 0 },
        { command: 'down', label: t('legend.move_down'), icon: 'arrow-down', disabled: index === this.items.length - 1 },
        { command: 'remove', label: t('legend.remove'), icon: 'trash', separatorBefore: true },
      ],
      onSelect: command => this.change(command === 'remove' ? { type: 'remove', id: item.id } : { type: 'move', id: item.id, offset: command === 'up' ? -1 : 1 }),
      onClose: () => { if (trigger.isConnected) trigger.focus({ preventScroll: true }); },
    });
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.stopViewport = trackLegendPickerViewport(this);
  }

  override disconnectedCallback(): void {
    this.stopViewport?.();
    this.closeMenu?.();
    super.disconnectedCallback();
  }

  private cancelAdding(): void {
    this.adding = false; this.error = ''; this.targets = []; this.draft = '';
    void this.updateComplete.then(() => this.renderRoot.querySelector<HTMLButtonElement>('.add')?.focus({ preventScroll: true }));
  }

  private addForm() {
    return renderLegendPicker({
      drawings: this.drawings, layers: this.layers, ids: this.targets, label: this.draft, items: this.items,
      toggle: id => { this.targets = this.targets.includes(id) ? this.targets.filter(target => target !== id) : [...this.targets, id]; this.error = ''; this.requestUpdate(); },
      rename: label => { this.draft = label; this.requestUpdate(); },
      submit: id => this.submit(id), cancel: () => this.cancelAdding(),
    });
  }

  private compactButton() {
    return this.canEdit || this.items.length ? html`<button class=${this.canEdit ? 'entry' : 'entry reader'} aria-label=${this.items.length ? t('legend.expand') : nothing} aria-expanded=${this.items.length ? 'false' : nothing} @click=${() => this.show()}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="5" cy="5" r="2"/><path d="M3 12h4M3 18h4v4H3zM12 5h9M12 12h9M12 20h9"/></svg><span>${t(!this.items.length && this.selected.length ? 'legend.add_selected' : 'legend.title')}</span></button>` : nothing;
  }

  private layerOption() {
    return this.canEdit && this.editing ? html`<label class="layer-option"><input type="checkbox" .checked=${this.showLayers}
      @change=${(event: Event) => { if (event.currentTarget instanceof HTMLInputElement) { this.adding = false; this.change({ type: 'layers', enabled: event.currentTarget.checked }); } }}>${t('legend.show_layers')}</label>` : nothing;
  }

  private renderPicker() {
    return html`<section class="card adding-card" aria-label=${t('legend.add_item')} @keydown=${(event: KeyboardEvent) => { if (event.key === 'Escape') this.cancelAdding(); }}>
      <header><h2>${t('legend.add_item')}</h2></header>
      ${this.addForm()}
      ${this.error ? html`<p role="alert" class="error">${this.error}</p>` : nothing}
    </section>`;
  }

  override render() {
    const compact = !this.editing && (!this.items.length || this.collapsed);
    if (compact) return this.compactButton();
    if (this.canEdit && this.adding) return this.renderPicker();
    return html`<section class="card" aria-label=${t('legend.title')} @keydown=${(event: KeyboardEvent) => { if (event.key === 'Escape') { this.editing = false; this.error = ''; } }}>
      <header><h2>${t('legend.title')}</h2><div class="actions">
        ${this.canEdit ? html`<button class="edit-control" @click=${() => this.editing ? this.finish() : this.open()}>${t(this.editing ? 'legend.done' : 'legend.edit')}</button>` : nothing}
        ${!this.editing ? html`<button aria-label=${t(this.collapsed ? 'legend.expand' : 'legend.collapse')} aria-expanded=${String(!this.collapsed)} @click=${() => { this.collapsed = !this.collapsed; }}>${this.collapsed ? '+' : '−'}</button>` : nothing}
      </div></header>
      ${this.layerOption()}
      ${!this.collapsed ? html`<ul>${repeat(this.items, item => item.id, (item, index) => this.row(item, index))}</ul>
        ${this.editing && !this.items.length ? html`<p class="empty">${t(this.showLayers ? 'legend.no_visible_layers' : 'legend.empty')}</p>` : nothing}
        ${this.canEdit && this.editing && !this.showLayers ? html`<button class="add" @click=${() => this.startAdding()}>+ ${t('legend.add_item')}</button>` : nothing}
        ` : nothing}
      ${this.error ? html`<p role="alert" class="error">${this.error}</p>` : nothing}
    </section>`;
  }
}

customElements.define('drawing-legend', DrawingLegendElement);
